import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, Observable, of, switchMap, tap } from 'rxjs';

import { IdValueType } from '@models/common';
import { Role } from '@models/module/main';
import { TransitionDialogComponent } from '@module-instance/dynamic-module/transition-dialog/transition-dialog.component';
import { ModuleInstanceCommonService } from '@module-instance/services/module-instance-common.service';
import { EntityTableRow } from '@shared/components/data-list/common';
import { InfoDialog } from '@shared/dialogs';
import { NotificationService, UserService } from '@shared/services';
import { EntityInstanceTransition } from '..';

export type TransitionRow = Pick<
  EntityTableRow,
  'entity_instance_id' | 'is_draft' | 'module_id' | 'contract_id' | 'entity_template_id'
>;

export type TransitionServiceParams = {
  rows: EntityTableRow[];
  contractId: number;
  transition: EntityInstanceTransition;
  workflowId?: number;
  role?: Role;
  startScreenValues?: IdValueType<any, number>[];
};

@Injectable({ providedIn: 'root' })
export class TransitionService {
  private moduleInstanceCommonService = inject(ModuleInstanceCommonService);
  private translate = inject(TranslateService);
  private userService = inject(UserService);
  private notify = inject(NotificationService);
  private dialog = inject(MatDialog);

  executeTransition(params: TransitionServiceParams): Observable<number[]> {
    // show info dialog
    const dialogRef = this.dialog.open(InfoDialog, {
      panelClass: ['cipo', 'cipo-dialog', 'classic'],
      ...this.userService.getResponsiveDialogSize().sm,
      disableClose: true,
      data: {
        title: 'dm.messages.autoTriggerTransition',
        description: 'dm.messages.autoTriggerTransitionDesc',
      },
    });

    const { rows, contractId, transition, workflowId, startScreenValues, role } = params;
    const presetIds = rows.filter(r => r.entity_template_id > 0).map(r => r.entity_template_id);
    const info = rows
      .filter(r => r.entity_instance_id > 0)
      .map(r => ({
        entityInstanceId: r.entity_instance_id,
        paymentPeriodId: r.payment_period_id,
        isDraft: r.is_draft,
      }));

    // trigger auto transition
    return this.moduleInstanceCommonService
      .triggerMultipleTransitionScreensOrTemplates({
        presetIds,
        info,
        contractId,
        workflowId,
        moduleId: rows[0].module_id,
        transitionId: transition.id,
        isAutoTrigger: transition.isAutoTrigger,
      })
      .pipe(
        switchMap(result => {
          // close info dialog, the request is done
          dialogRef.close();

          // we have missing required fields
          if ((result.fullDocNumsWithRequiredFields ?? []).length) {
            // show missing fields notification
            this.handleMissingFields(result.fullDocNumsWithRequiredFields);
            return of([]);
          }

          // we have successful auto trigger
          if ((result.entityInstanceIds ?? []).length) {
            // return the list of entityInstanceIds
            return of(result.entityInstanceIds);
          }

          // here we either have auto trigger off or signature is required
          return this.dialog
            .open(TransitionDialogComponent, {
              panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full'],
              ...this.userService.getResponsiveDialogSize().lg,
              data: {
                rows,
                presetIds,
                info,
                transition,
                signatureIsRequired: result.signatureIsRequired ?? false,
                workflowId,
                startScreenValues,
                role,
              },
            })
            .afterClosed()
            .pipe(filter((ids: number[]) => !!ids?.length));
        }),
        /* Skip error to close the autoTrigger dialog */
        catchError(() => of(null)),
        tap(() => dialogRef.close()),
      );
  }

  private handleMissingFields(missingFields: string[]) {
    if (missingFields?.length) {
      const message = this.translate.instant('dm.messages.missingRequiredFields', {
        ids: missingFields.join(', '),
      });
      this.notify.error(message);
    }
  }
}
